import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react'
import { ReactComponent as ChatbotReportsIcon } from "../../../assets/ChatbotReportsSvgs/ChatbotReportsIcon.svg";
import { ReactComponent as CloseChatbotReportsIcon } from "../../../assets/ChatbotReportsSvgs/CloseChatbotReportsIcon.svg";
import { ReactComponent as SendButtonIcon } from "../../../assets/ChatbotReportsSvgs/SendButtonIconChatbot.svg";
import { ReactComponent as ChatbotWindowCloseIcon } from "../../../assets/ChatbotReportsSvgs/ChatbotWindowCloseIcon.svg";
import { ReactComponent as ExpandChatbotIcon } from "../../../assets/ChatbotReportsSvgs/ExpandChatbotIcon.svg";
import { ReactComponent as MaghilChatbotLogo } from "../../../assets/ChatbotReportsSvgs/MaghilChatbotLogo.svg";
import { ReactComponent as CollapseChatbotIcon } from "../../../assets/ChatbotReportsSvgs/CollapseChatbotIcon.svg";
import { ReactComponent as ChatHistoryIcon } from "../../../assets/ChatbotReportsSvgs/ChatHistoryIcon.svg";
import { ReactComponent as BackButtonChatbotIcon } from "../../../assets/ChatbotReportsSvgs/BackButtonChatbotIcon.svg";
import UserQueryMessageBubble from './UserQueryMessageBubble';
import { usePCChatbot } from '../hooks/usePCChatbot';
import { useSpeechToText } from '../hooks/useSpeechToText';
import { PCMessageView } from '../components/PCMessageView';
import { PCActivityLogPanel } from '../components/PCActivityLogPanel';
import { PCIntro } from '../components/PCIntro';
import { PCThreadRow } from '../components/PCThreadRow';
import { Icon as BrisqueIcon, dayBucket } from '../components/atoms';
import { useBranch } from '../../../context/BranchContext';
import './style.scss'
import { ChatbotReportsPropsInterface } from '../../../interface/ReportsChatbotInterface';

const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia(query).matches : false
    );
    useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
        mql.addEventListener('change', handler);
        setMatches(mql.matches);
        return () => mql.removeEventListener('change', handler);
    }, [query]);
    return matches;
}

/**
 * Product Catalog Control Center AI chatbot (Brisque AI).
 *
 * Catalogue-only: every message is sent to the Brisque backend
 * (`POST /conversations/:id/messages`) which returns the message stream (§5).
 * All state lives in the `ProductCatalogChatbot` Redux slice, driven through the
 * `usePCChatbot` hook. This chatbot no longer talks to the reports backend.
 */
const ProductCatalogAIChatbot = ({
    headerTextReportsChatBot = "Brisque AI",
    centerTextReportsChatBot = "Ask about item availability, 86/OOS, channels and sessions.",
    messageInputPlaceholderReportsChatBot = "Ask me anything...",
    footerTextReportsChatBot = "AI may make mistakes, Check your important info",
    enableVoiceInputReportsChatBot = true,
    enableFileUploadReportsChatBot = false,
    chatbotReportsName = "Brisque AI",
    defaultOpen = false,
    onClose,
}: ChatbotReportsPropsInterface) => {
    const chat = usePCChatbot();
    const { selectedBranch } = useBranch();

    const locationLabel = useMemo(
        () => selectedBranch?.locationName ?? selectedBranch?.name ?? '',
        [selectedBranch]
    );
    const city = locationLabel?.split(', ')[1] || locationLabel;

    const isMobile = useMediaQuery('(max-width: 768px)');
    // The mobile header inverts to a white bar (style.scss), so the header actions can't
    // stay white or they vanish into it — match the grey the history/close icons use there.
    const headerIconColor = isMobile ? '#6F6F6F' : '#fff';

    const [isChatbotWindowExpanded, setIsChatbotWindowExpanded] = useState<boolean>(false);
    const [isChatbotWindowOpen, setIsChatbotWindowOpen] = useState<boolean>(defaultOpen);
    const [inputValue, setInputValue] = useState<string>("");
    // Launcher position while the user has dragged it (null = CSS default spot). Same
    // behaviour as the Maghil Insights launcher in ChatbotReports.
    const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
    const isDraggingRef = useRef(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const launcherRef = useRef<HTMLDivElement>(null);
    const [activityOpen, setActivityOpen] = useState<boolean>(false);
    const [historyView, setHistoryView] = useState<'none' | 'listing'>('none');
    // The thread that was active before the user opened one out of the history list. Opening a
    // past chat swaps it into the main view, which otherwise looks identical to the live one -
    // no back affordance, and no way to get to the conversation they were in. Held here so the
    // Back button can put it back. Null whenever the user is already in their current chat.
    const [returnToConversationId, setReturnToConversationId] = useState<string | null>(null);
    // Keyed on the error's sequence number, not its text: pressing the same failing action
    // twice raises the identical message, and remembering the STRING hid every repeat.
    const [dismissedErrorSeq, setDismissedErrorSeq] = useState<number | null>(null);

    // Voice input (§9.3): dictation lands as editable text in the composer.
    const voice = useSpeechToText((transcript) => setInputValue(transcript));
    const scrollBottomRef = useRef<HTMLDivElement>(null);
    // Set by a successful undo; consumed once the chat is visible again (see below).
    const scrollAfterUndoRef = useRef(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Auto-grow the composer with its content (up to the CSS max-height, after which
    // it scrolls internally). Runs on every value change, including programmatic ones
    // (voice dictation, send-clears the box), and whenever the panel is opened or the
    // composer comes back from the history/activity views.
    //
    // The EMPTY box is never sized by a measurement. An empty composer is exactly what a
    // reopened panel shows, and `scrollHeight` read before the panel has settled layout
    // (0 while hidden, and `style.height = 'auto'` alone already collapses a rows={1}
    // textarea) pinned a too-small inline height that only the next keystroke corrected -
    // the reported "shrinks on reopen, springs back once you type". So: drop the inline
    // height whenever there is nothing to measure and let the CSS one-row min-height own
    // that state, which makes first open, reopen and post-send pixel-identical. Measure
    // only when there IS content. useLayoutEffect applies it before paint, so reopening
    // never flashes an intermediate size.
    useLayoutEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        // offsetParent is null inside a display:none subtree (the history listing and the
        // activity log both hide the composer) and a 0-width box has no usable layout either.
        if (el.offsetParent === null || el.clientWidth === 0 || el.value === '') {
            el.style.height = '';
            return;
        }
        el.style.height = 'auto';
        if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
    }, [inputValue, isChatbotWindowOpen, historyView, activityOpen]);

    const conversation = chat.state.conversation;
    const conversationId = chat.state.activeConversationId;
    // Memoized so the `?? []` fallback doesn't hand back a NEW array identity every render —
    // `messages` is a dependency of handleSendMessage, and an unstable one would rebuild that
    // callback (and re-render the composer) on every single render.
    const messages = useMemo(() => conversation?.messages ?? [], [conversation?.messages]);
    const hasUserMessage = messages.some((m) => m.kind === 'user');
    // An ACTIONABLE card is one still waiting on the user: a disambiguation picker (choose one
    // of N) or a pending confirm card (Confirm / Cancel). While one is up, the composer is
    // locked so the flow can't fork into an ambiguous half-answered state — the card itself is
    // the only way forward, and every actionable card carries a Cancel that releases the lock.
    // Terminal cards ('committed'/'cancelled') and non-action cards never block anything.
    // Only the MOST RECENT actionable card blocks the composer. Several can be on screen at
    // once (a fix suggestion, a picker, a proposed change), but the server tracks just one as
    // live — so acting on the newest can leave an older one still rendered as 'pending'. Taking
    // the FIRST match meant that stale card kept the lock on: its own buttons only returned
    // "already handled", so there was no way to type and no way to clear it. Scanning from the
    // end ties the lock to the card the user is actually being asked about.
    const blockingCard = [...messages].reverse().find(
        (m) => m.kind === 'action' && (m.card.status === 'pending' || m.card.status === 'disambiguation')
    );
    const awaitingCardAction = !!blockingCard;
    const awaitingPicker = blockingCard?.kind === 'action' && blockingCard.card.status === 'disambiguation';

    // `syncing` spans the whole trigger → countdown → results window (the saga now owns it
    // end to end) — a message sent mid-sync would race the pending catalogue refresh, so the
    // composer stays blocked exactly like it does for sending/card actions.
    const isWaitingForResponse = chat.state.sending || chat.state.cardActionLoading || chat.state.syncing;
    const isComposerLocked = isWaitingForResponse || awaitingCardAction;

    const iconColor = inputValue?.length > 0 ? '#E52333' : isComposerLocked ? '#999' : '#a8a8a8';

    // On open (and again whenever the selected location changes while the window stays
    // open): resume/create the active thread + load channels and the sidebar list. A
    // location switch clears the slice back to initialState (usePCChatbot), so this also
    // re-syncs everything for the new location instead of leaving the panel empty.
    // Which location the channel/CTA lists were actually fetched for. `channelsLoaded` alone is
    // not enough on an outlet switch: the reset that clears the slice lives in usePCChatbot (a
    // CHILD effect, which React runs before this parent one) and its dispatch doesn't reach
    // `chat.state` until the next render - so this effect still saw the OLD `channelsLoaded:
    // true`, skipped the fetch, and was then wiped to an empty channel list that nothing
    // refetched. Every channel id in every card then rendered as a raw UUID.
    const loadedForLocationRef = useRef<string | null>(null);
    useEffect(() => {
        if (!isChatbotWindowOpen || !chat.ready) return;
        const locationChanged = loadedForLocationRef.current !== chat.identity.locationId;
        if (locationChanged || !chat.state.channelsLoaded) chat.fetchChannels();
        if (locationChanged || !chat.state.ctasLoaded) chat.fetchCtas();
        loadedForLocationRef.current = chat.identity.locationId;
        chat.listConversations();
        if (!chat.state.activeConversationId && !chat.state.createLoading) {
            chat.createConversation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChatbotWindowOpen, chat.ready, chat.identity.locationId]);

    // Auto-scroll to bottom on new messages, but not on the intro screen.
    useEffect(() => {
        if (!hasUserMessage) return;
        scrollBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length, hasUserMessage]);

    /**
     * `fromAffordance` marks a tap on a bot-offered button (a diagnostic's "Show X on Delivery"
     * fix, a blocked-write notice's "Show the parent category"), as opposed to typed text.
     *
     * Those buttons were dead whenever a card was on screen: the composer lock exists so free
     * typing can't fork a half-answered flow, but it also swallowed the button's own send and
     * the tap did nothing at all — no message, no error, no card. A tap is not an ambiguous
     * sentence: it is the exact instruction the bot itself just proposed, so it bypasses the
     * CARD lock. It still respects the in-flight lock (`isWaitingForResponse`), because firing
     * a second request while one is mid-flight is a real race, not a UX nicety.
     */
    const handleSendMessage = useCallback((text?: string, fromAffordance = false) => {
        const messageText = (text ?? inputValue).trim();
        const blocked = fromAffordance ? isWaitingForResponse : isComposerLocked;
        if (!messageText || blocked) return;
        if (!conversationId) {
            // Thread not ready yet — create it; the user can resend once it lands.
            if (chat.ready && !chat.state.createLoading) chat.createConversation();
            return;
        }
        // Typing the button's own words is the same intent as tapping it, so run the SAME flow
        // (trigger → countdown → results) rather than sending it to the agent, which has no sync
        // tool and can only describe the button. Deliberately an EXACT match on the affordance's
        // label — not a guess at phrasings: anything else ("push it live", "yes go ahead") goes
        // to the agent, which understands it properly and points at the button. Only fires while
        // an unsynced offer is actually on screen.
        const syncOffer = [...messages].reverse().find((m) => m.kind === 'sync' && m.card.status === 'offer');
        const typedTheButton = ['sync', 'sync now'].includes(messageText.toLowerCase().replace(/[.!]+$/, ''));
        if (syncOffer && typedTheButton) {
            chat.appendLocalMessage({ kind: 'user', id: generateId(), text: messageText });
            chat.runSync(conversationId, syncOffer.id);
            setInputValue("");
            return;
        }
        // Optimistic user bubble (§5.1) — the server response returns only bot messages.
        chat.appendLocalMessage({ kind: 'user', id: generateId(), text: messageText });
        chat.sendMessage(conversationId, messageText);
        setInputValue("");
    }, [inputValue, isComposerLocked, isWaitingForResponse, conversationId, chat, messages]);

    // A successful undo from the activity log routes the user to the chat it was written into —
    // the backend appends an "Undone: …" bubble plus a fresh sync offer there, and that thread is
    // where the follow-up (syncing the reversal) happens. A REJECTED undo (`ok: false`) never
    // sets this, so the user stays in the activity log with the reason on screen. One effect on
    // the single shell, so it behaves identically on mobile and desktop.
    useEffect(() => {
        if (!chat.state.undoNavConversationId) return;
        scrollAfterUndoRef.current = true;
        setActivityOpen(false);
        setHistoryView('none');
        chat.clearUndoNav();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chat.state.undoNavConversationId]);

    // The "Undone: …" messages land while the chat is hidden behind the activity log, so the
    // auto-scroll above can't move it, and the chat reopens at the top. Jump to the end once
    // it is visible and loaded.
    useEffect(() => {
        if (!scrollAfterUndoRef.current || activityOpen || historyView !== 'none' || chat.state.conversationLoading) return;
        scrollAfterUndoRef.current = false;
        scrollBottomRef.current?.scrollIntoView({ behavior: 'auto' });
    }, [activityOpen, historyView, chat.state.conversationLoading, messages.length]);


    // The open panel is anchored at its own fixed spot, so the launcher (now the close
    // button) goes back to its default position while the panel is open.
    useEffect(() => {
        if (isChatbotWindowOpen) setDragPos(null);
    }, [isChatbotWindowOpen]);

    // Keep a dragged launcher on screen if the viewport shrinks (rotation, window resize).
    useEffect(() => {
        if (!dragPos) return;
        const clamp = () => setDragPos((p) => p && {
            x: Math.max(0, Math.min(p.x, window.innerWidth - 64)),
            y: Math.max(0, Math.min(p.y, window.innerHeight - 64)),
        });
        window.addEventListener('resize', clamp);
        return () => window.removeEventListener('resize', clamp);
    }, [dragPos]);

    // Auto-avoid bottom bars: probe the launcher's DEFAULT box; if a fixed/sticky element
    // that isn't ours is painted there, lift the launcher to sit just above it. Re-probed on
    // DOM / scroll / resize changes, so it drops back to the corner when the footer goes away.
    // A user-dragged launcher is never auto-moved.
    //
    // The lift lives ONLY on the DOM (`--bot-lift` on the root), never in React state. The
    // probe derives the default box from the lift actually applied to the element it measures.
    // With React state, the style write fired the MutationObserver while the previous effect
    // (closure still holding the OLD lift) was subscribed: it measured the already-lifted
    // launcher as if it were the default spot, saw no footer, reset to 0, and the launcher
    // flipped up/down forever (the Control Center bulk-selection bar).
    useEffect(() => {
        const rootEl = rootRef.current;
        if (defaultOpen || dragPos) { rootEl?.style.removeProperty('--bot-lift'); return; }
        let raf = 0;
        const probe = () => {
            raf = 0;
            const el = launcherRef.current;
            const root = rootRef.current;
            if (!el || !root) return;
            const r = el.getBoundingClientRect();
            if (r.width === 0) return;
            const base = parseFloat(root.style.getPropertyValue('--bot-lift')) || 0;
            // Default box = current box moved back down by the lift that is applied right now.
            const box = { l: r.left, r: r.right, t: r.top + base, b: r.bottom + base };
            const vh = window.innerHeight;
            const pts: Array<[number, number]> = [
                [(box.l + box.r) / 2, (box.t + box.b) / 2],
                [box.l + 6, box.t + 6], [box.r - 6, box.t + 6],
                [box.l + 6, box.b - 6], [box.r - 6, box.b - 6],
            ];
            let top = Infinity;
            for (const [x, y] of pts) {
                if (y < 0 || y > vh) continue;
                for (const hit of document.elementsFromPoint(x, y)) {
                    if (root.contains(hit)) continue;
                    for (let n: Element | null = hit; n && n !== document.body; n = n.parentElement) {
                        const cs = getComputedStyle(n);
                        if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
                        // Sticky-LEFT table cells (Control Center's colSpan group rows) and
                        // sticky-top headers are not footers - only bottom-anchored sticky counts.
                        if (cs.position === 'sticky' && cs.bottom === 'auto') continue;
                        const nr = n.getBoundingClientRect();
                        // A footer is a wide bar that reaches the launcher's bottom edge. That rules
                        // out hover popovers / menus that merely float near the corner, FABs, and
                        // full-screen overlays/modals.
                        const isFooter = nr.width >= window.innerWidth * 0.4
                            && nr.height < vh / 2
                            && nr.top > vh / 2
                            && nr.bottom >= box.b - 4;
                        if (isFooter) { top = Math.min(top, nr.top); break; }
                    }
                }
            }
            const next = top === Infinity ? 0 : Math.max(0, Math.round(box.b - top + 12));
            if (next === base) return;
            if (next) root.style.setProperty('--bot-lift', `${next}px`);
            else root.style.removeProperty('--bot-lift');
        };
        const schedule = () => { if (!raf) raf = requestAnimationFrame(probe); };
        schedule();
        const mo = new MutationObserver(schedule);
        mo.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class'] });
        window.addEventListener('resize', schedule);
        window.addEventListener('scroll', schedule, true);
        return () => {
            mo.disconnect();
            window.removeEventListener('resize', schedule);
            window.removeEventListener('scroll', schedule, true);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [defaultOpen, dragPos]);

    const handleToggleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
        const point = (ev: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent) =>
            'touches' in ev ? ev.touches[0] : (ev as MouseEvent);
        const start = point(e);
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const startX = start.clientX;
        const startY = start.clientY;
        let dragged = false;

        const onMove = (ev: MouseEvent | TouchEvent) => {
            const p = point(ev);
            const dx = p.clientX - startX;
            const dy = p.clientY - startY;
            if (!dragged && (Math.abs(dx) > 5 || Math.abs(dy) > 5)) dragged = true;
            if (!dragged) return;
            isDraggingRef.current = true;
            if (ev.cancelable) ev.preventDefault();
            setDragPos({
                x: Math.max(0, Math.min(rect.left + dx, window.innerWidth - 64)),
                y: Math.max(0, Math.min(rect.top + dy, window.innerHeight - 64)),
            });
        };
        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            document.removeEventListener('touchmove', onMove);
            document.removeEventListener('touchend', onUp);
            document.removeEventListener('touchcancel', onUp);
            // Defer so the click that follows a drag release is still swallowed.
            if (isDraggingRef.current) setTimeout(() => { isDraggingRef.current = false; }, 0);
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        document.addEventListener('touchmove', onMove, { passive: false });
        document.addEventListener('touchend', onUp);
        document.addEventListener('touchcancel', onUp);
    }, []);

    const handleNewChat = useCallback(() => {
        if (voice.listening) voice.stop();
        setActivityOpen(false);
        setHistoryView('none');
        setReturnToConversationId(null); // a brand-new thread is the current one; nothing to go back to
        setInputValue('');
        if (chat.ready) chat.createConversation(true);
    }, [chat, voice]);

    // Enter sends; Shift+Enter (and Ctrl/Cmd+Enter) inserts a newline — the modern
    // multiline-chat convention. `onKeyDown` (not `onKeyPress`, which is deprecated and
    // doesn't reliably expose the shift modifier on all browsers for textareas).
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="product-catalog-ai-chatbot" ref={rootRef}>
            {!defaultOpen && (
                <div
                    className="toggle-chatbot-window-container"
                    ref={launcherRef}
                    onMouseDown={!isChatbotWindowOpen ? handleToggleDragStart : undefined}
                    onTouchStart={!isChatbotWindowOpen ? handleToggleDragStart : undefined}
                    style={!isChatbotWindowOpen && dragPos
                        ? { left: dragPos.x, top: dragPos.y, bottom: 'auto', right: 'auto' }
                        : undefined}
                >
                    {!isChatbotWindowOpen ? (
                        <div className="open-chatbot-window">
                            <ChatbotReportsIcon onClick={() => { if (!isDraggingRef.current) setIsChatbotWindowOpen(true); }} />
                        </div>
                    ) : (
                        <div className="close-chatbot-window">
                            <CloseChatbotReportsIcon onClick={() => setIsChatbotWindowOpen(false)} />
                        </div>
                    )}
                </div>
            )}
            {isChatbotWindowOpen ? (
                <div className={`reports-chatbot-container ${isChatbotWindowExpanded ? 'expanded' : ''}`}>
                    <div className='reports-chatbot-header-container'>
                        <div className='maghil-insights-logo-and-text-container'>
                            {historyView === 'listing' ? (
                                <>
                                    <BackButtonChatbotIcon className='back-btn-maghil-chatbot' onClick={() => setHistoryView('none')} />
                                    <div className='maghil-insights-text'>Your chats</div>
                                </>
                            ) : returnToConversationId ? (
                                <>
                                    <BackButtonChatbotIcon
                                        className='back-btn-maghil-chatbot'
                                        onClick={() => {
                                            chat.loadConversation(returnToConversationId);
                                            setReturnToConversationId(null);
                                        }}
                                    />
                                    <div className='maghil-insights-text'>Past chat</div>
                                </>
                            ) : (
                                <>
                                    <MaghilChatbotLogo className="maghil-chatbot-logo" />
                                    <div className='maghil-insights-text'>{headerTextReportsChatBot}</div>
                                </>
                            )}
                        </div>
                        <div className='expand-minimize-close-buttons-container'>
                            {/* No manual "update latest data" button: the catalogue is refreshed
                                automatically whenever the assistant is opened (see the effect
                                above), so there is nothing left for the user to click. */}
                            {historyView === 'none' && (
                                <span role="button" title="New chat" onClick={handleNewChat}
                                    style={{ display: 'inline-flex', cursor: 'pointer', color: headerIconColor, marginRight: 2 }}>
                                    <BrisqueIcon name="add_comment" size={20} color={headerIconColor} />
                                </span>
                            )}
                            {historyView === 'none' && (
                                <span role="button" title="Activity log — changes you've made"
                                    onClick={() => { setActivityOpen((v) => !v); setHistoryView('none'); }}
                                    style={{ display: 'inline-flex', cursor: 'pointer', color: headerIconColor, marginRight: 2, opacity: activityOpen ? 1 : 0.92 }}>
                                    <BrisqueIcon name="receipt_long" size={20} color={headerIconColor} />
                                </span>
                            )}
                            <ChatHistoryIcon className='chatbot-history-icon'
                                onClick={() => { setActivityOpen(false); setHistoryView('listing'); chat.listConversations(); }} />
                            {!isChatbotWindowExpanded && !isMobile ?
                                <ExpandChatbotIcon onClick={() => setIsChatbotWindowExpanded(true)} /> : !isMobile ?
                                    <CollapseChatbotIcon onClick={() => setIsChatbotWindowExpanded(false)} /> : <></>}
                            <ChatbotWindowCloseIcon className="chatbot-window-close-icon"
                                onClick={() => defaultOpen ? onClose?.() : setIsChatbotWindowOpen(false)} />
                        </div>
                    </div>
                    {chat.state.error && chat.state.errorSeq !== dismissedErrorSeq && (
                        // Errors (e.g. a failed delete/rename that 404s server-side) were
                        // previously silent — nothing rendered chat.state.error anywhere, so a
                        // failed action just looked like it did nothing. Dismissible; a NEW
                        // error (different message) always reappears even if the last one was
                        // dismissed, since dismissal only remembers that exact message.
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 16px', background: 'rgba(220,38,38,0.08)', color: 'var(--red, #DC2626)', fontSize: 12.5, fontFamily: 'var(--font-primary)', flexShrink: 0 }}>
                            <BrisqueIcon name="error" size={15} color="var(--red, #DC2626)" style={{ marginTop: 1, flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>{chat.state.error}</div>
                            <button type="button" onClick={() => setDismissedErrorSeq(chat.state.errorSeq)} title="Dismiss" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--red, #DC2626)', display: 'flex', flexShrink: 0 }}>
                                <BrisqueIcon name="close" size={15} />
                            </button>
                        </div>
                    )}
                    <div className='reports-chatbot-body-container'>
                        {historyView === 'listing' && (
                            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: 'var(--surface)' }}>
                                {chat.state.conversations.length === 0 ? (
                                    <div style={{ padding: '40px 28px', textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
                                        {chat.state.conversationsLoading ? 'Loading your chats…' : 'No chats yet.'}
                                    </div>
                                ) : (
                                    <>
                                        {/* Grouped under day headings ("Today", "Yesterday",
                                            "Previous 7 days"), the way chat products lay out a
                                            long history — a flat run of a hundred rows is far
                                            harder to scan for "the one from last Tuesday". */}
                                        {chat.state.conversations.map((c, i) => {
                                            const bucket = dayBucket(c.updatedAt);
                                            const prev = i > 0 ? dayBucket(chat.state.conversations[i - 1].updatedAt) : null;
                                            return (
                                                <React.Fragment key={c.conversationId}>
                                                    {bucket !== prev && (
                                                        <div style={{
                                                            position: 'sticky', top: 0, zIndex: 1,
                                                            padding: '8px 16px 6px', background: 'var(--surface)',
                                                            fontSize: 10.5, fontWeight: 700, letterSpacing: 0.5,
                                                            textTransform: 'uppercase', color: 'var(--t3)',
                                                            borderBottom: '1px solid var(--divider)',
                                                        }}>
                                                            {bucket}
                                                        </div>
                                                    )}
                                                    <PCThreadRow
                                                        thread={c}
                                                        onOpen={() => {
                                                            // Remember where to come back to, unless they picked
                                                            // the chat they were already in.
                                                            setReturnToConversationId(
                                                                chat.state.activeConversationId && chat.state.activeConversationId !== c.conversationId
                                                                    ? chat.state.activeConversationId
                                                                    : null
                                                            );
                                                            chat.loadConversation(c.conversationId);
                                                            setHistoryView('none');
                                                        }}
                                                        onRename={(title) => chat.rename(c.conversationId, title)}
                                                        onDelete={() => chat.deleteThread(c.conversationId)}
                                                    />
                                                </React.Fragment>
                                            );
                                        })}
                                        {chat.state.conversationsHasMore && (
                                            <button
                                                type="button"
                                                disabled={chat.state.conversationsLoading}
                                                onClick={() => {
                                                    const oldest = chat.state.conversations[chat.state.conversations.length - 1];
                                                    if (oldest) chat.loadOlderConversations(oldest.updatedAt);
                                                }}
                                                style={{
                                                    width: '100%', padding: '13px 16px', border: 'none',
                                                    borderTop: '1px solid var(--divider)', background: 'transparent',
                                                    color: 'var(--accent)', fontFamily: 'var(--font-primary)',
                                                    fontSize: 12.5, fontWeight: 600,
                                                    cursor: chat.state.conversationsLoading ? 'default' : 'pointer',
                                                    opacity: chat.state.conversationsLoading ? 0.55 : 1,
                                                }}
                                            >
                                                {chat.state.conversationsLoading ? 'Loading…' : 'Show older chats'}
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                        {activityOpen && historyView === 'none' && (
                            <PCActivityLogPanel chat={chat} onClose={() => setActivityOpen(false)} />
                        )}
                        <div className="chat-body-scroll-area" style={{ display: (historyView !== 'none' || activityOpen) ? 'none' : undefined }}>
                            {!chat.ready ? (
                                <div className="chatbot-body-initial-state">
                                    <ChatbotReportsIcon />
                                    <div className='chatbot-body-text'>Select a location to start using the Product Catalog assistant.</div>
                                </div>
                            ) : chat.state.conversationLoading || chat.state.createLoading ? (
                                // Switching to a different thread (conversationLoading) or starting a
                                // fresh one (createLoading) — a brief interstitial instead of silently
                                // swapping the message list, which would otherwise flash the PREVIOUS
                                // thread's messages until the new ones land.
                                <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 9, color: 'var(--t3)', fontSize: 13 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }} />
                                    {chat.state.createLoading ? 'Starting a new chat…' : 'Loading chat…'}
                                </div>
                            ) : !hasUserMessage ? (
                                <div className="chat-messages-container">
                                    {locationLabel && <div className="date-separator">{city}</div>}
                                    <PCIntro onAsk={handleSendMessage} ctas={chat.state.ctas} />
                                    {/* Render any resumed non-user messages below the intro */}
                                    {messages.map((msg) => msg.kind === 'user' ? null : (
                                        <div key={msg.id} style={{ width: '100%', margin: '2px 0' }}>
                                            <PCMessageView msg={msg} chat={chat} conversationId={conversationId ?? ''} onSend={handleSendMessage} />
                                        </div>
                                    ))}
                                    <div ref={scrollBottomRef} />
                                </div>
                            ) : (
                                <div className="chat-messages-container">
                                    <div className="date-separator">{city}</div>
                                    {messages.map((msg) => {
                                        if (msg.kind === 'user') {
                                            return <UserQueryMessageBubble key={msg.id} text={msg.text} />;
                                        }
                                        return (
                                            <div key={msg.id} style={{ width: '100%', margin: '2px 0' }}>
                                                <PCMessageView msg={msg} chat={chat} conversationId={conversationId ?? ''} onSend={handleSendMessage} />
                                            </div>
                                        );
                                    })}
                                    {isWaitingForResponse && !chat.state.syncing && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', color: 'var(--t3)', fontSize: 12.5, fontFamily: 'var(--font-primary)' }}>
                                            <BrisqueIcon name="auto_awesome" size={15} color="var(--accent)" /> {chat.state.thinkingStatus ?? 'Thinking…'}
                                        </div>
                                    )}
                                    <div ref={scrollBottomRef} />
                                </div>
                            )}
                        </div>

                        <div className="suggestion-and-input-container" style={{ marginTop: 'auto', display: (historyView !== 'none' || activityOpen) ? 'none' : undefined }}>
                            <div className={`message-input-box-container-reports-chatbot${isComposerLocked ? ' is-disabled' : ''}`}>
                                <style>{`@keyframes briskMicPulse{0%,100%{box-shadow:0 0 0 0 rgba(229,35,51,0.45)}50%{box-shadow:0 0 0 6px rgba(229,35,51,0)}}`}</style>
                                <textarea
                                    ref={inputRef}
                                    rows={1}
                                    placeholder={
                                        voice.listening ? 'Listening… speak now'
                                            : awaitingPicker ? 'Pick one of the options above, or Cancel'
                                                : awaitingCardAction ? 'Confirm or Cancel the change above'
                                                    : isWaitingForResponse ? 'Waiting for response...'
                                                        : messageInputPlaceholderReportsChatBot
                                    }
                                    value={inputValue}
                                    onChange={(e) => { setInputValue(e.target.value); if (voice.error) voice.clearError(); }}
                                    onKeyDown={handleKeyDown}
                                    disabled={isComposerLocked}
                                />
                                {enableVoiceInputReportsChatBot && voice.supported && (
                                    <span role="button" title={voice.listening ? 'Stop listening' : 'Speak your request'}
                                        onClick={() => { if (voice.listening) voice.stop(); else voice.start(); }}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
                                            color: voice.listening ? '#fff' : '#a8a8a8',
                                            background: voice.listening ? '#E52333' : 'transparent',
                                            animation: voice.listening ? 'briskMicPulse 1.3s infinite' : 'none',
                                            transition: 'background .15s, color .15s',
                                        }}>
                                        <BrisqueIcon name={voice.listening ? 'stop' : 'mic'} size={18} color={voice.listening ? '#fff' : '#a8a8a8'} />
                                    </span>
                                )}
                                <SendButtonIcon className="send-btn-icon" onClick={() => handleSendMessage()}
                                    style={{ opacity: isComposerLocked ? 0.6 : 1, pointerEvents: isComposerLocked ? 'none' : 'auto', color: iconColor }} />
                            </div>
                            <div className="footer-chatbot-container" style={voice.error ? { color: 'var(--orange, #D97706)' } : undefined}>
                                {voice.error || (voice.listening ? 'Listening… tap the mic again to stop, then review before sending.' : footerTextReportsChatBot)}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ProductCatalogAIChatbot;
